import { Score, difficultyLabels, categoryLabels } from '../../../mocks/scores';

interface ScoreCardProps {
  score: Score;
  onClick: (score: Score) => void;
  index: number;
  isVisible: boolean;
}

const difficultyColors: Record<string, string> = {
  beginner: 'bg-emerald-50 text-emerald-700',
  intermediate: 'bg-amber-50 text-amber-700',
  advanced: 'bg-rose-50 text-rose-800',
};

export default function ScoreCard({ score, onClick, index, isVisible }: ScoreCardProps) {
  return (
    <div
      className={`group cursor-pointer transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
      onClick={() => onClick(score)}
    >
      {/* Image Area */}
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-4">
        <img
          src={score.imageUrl}
          alt={score.title}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-sumi/0 group-hover:bg-sumi/30 transition-all duration-300 flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-sans text-xs text-white tracking-widest uppercase border border-white/60 px-4 py-2 rounded-sm whitespace-nowrap">
            詳細を見る
          </span>
        </div>

        {/* Category badge */}
        <div className="absolute top-3 left-3">
          <span className="font-sans text-xs bg-white/90 text-sumi px-2 py-1 rounded-sm tracking-wide whitespace-nowrap">
            {categoryLabels[score.category]}
          </span>
        </div>

        {/* Ensemble badge */}
        {score.ensemble && (
          <div className="absolute top-3 right-3">
            <span className="font-sans text-xs bg-sumi/80 text-white px-2 py-1 rounded-sm tracking-wide whitespace-nowrap">
              Ensemble {score.ensemble}
            </span>
          </div>
        )}

        {/* Audio sample badge */}
        {score.audioSample && (
          <div className="absolute bottom-3 right-3">
            <span className="font-sans text-xs bg-white/90 text-sumi px-2 py-1 rounded-sm tracking-wide flex items-center gap-1 whitespace-nowrap">
              <i className="ri-music-2-line text-xs"></i>
              試聴あり
            </span>
          </div>
        )}
      </div>

      {/* Info Area */}
      <div className="space-y-2">
        {/* Title */}
        <div>
          <h3 className="font-serif text-lg text-sumi leading-tight">{score.title}</h3>
          <p className="font-sans text-xs text-sumi/50 tracking-wider mt-0.5">{score.titleReading}</p>
        </div>

        {/* Subtitle */}
        <p className="font-sans text-sm text-sumi/70">{score.subtitle}</p>

        {/* Instrumentation */}
        <div className="flex flex-wrap gap-1">
          {score.instrumentation.map((inst) => (
            <span key={inst} className="font-sans text-xs bg-washi text-sumi/70 px-2 py-0.5 rounded-sm whitespace-nowrap">
              {inst}
            </span>
          ))}
        </div>

        {/* Bottom row */}
        <div className="flex items-center justify-between pt-1">
          <span className={`font-sans text-xs px-2 py-0.5 rounded-sm whitespace-nowrap ${difficultyColors[score.difficulty]}`}>
            {difficultyLabels[score.difficulty]}
          </span>
          <span className="font-sans text-base font-semibold text-sumi whitespace-nowrap">
            ¥{score.price.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}
